import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { useAccountStore } from '@/stores/account';
import { useWalletStore } from '@/stores/wallet';
import {
  validateOnboardingAccess,
  verifyKycStatus,
} from '../helpers/routeBefore';
import { isPaymentCallbackRoute } from '../helpers/isPaymentCallbackRoute';

import { authClient } from '@/api';
import { logOut } from '@/helpers/auth';
import { canCompanyRecharge } from '@/helpers/country';
import { isCompanyComplete } from '@/helpers/kyc';
import { isCompanyGlobal81Chile } from '@/modules/dashboard/utils/shareholdersCountry.util';
import { toast } from '@flash-global66/g-toast';
import {
  SHAREHOLDERS_MODAL_COUNTRY_CO,
  SHAREHOLDERS_MODAL_EXCLUDED_COUNTRIES,
  SHAREHOLDERS_MODAL_WHITELISTED_COMPANY_IDS,
  SHAREHOLDERS_VERIFICATION_ERROR_MESSAGE,
} from '@/modules/dashboard/constants/shareholdersModal.constant';
import { getBankPartnerStatus } from '@/modules/cashIn/utils/getBankPartnerStatus.util';
import { BRIDGE_ACCOUNT_PARTNERS } from '@/modules/cashIn/constants/bridge.constants';
import type { WalletType } from '@/types/WalletType';
import { useRemoteConfigStore } from '@/shared/firebase';
import { ADMIN_USER_LEVEL } from '@/shared/common/constants/userLevels.constant';
import { useAccessExclusions } from '@/shared/access/composables/useAccessExclusions';

function canWalletRecharge(wallet: WalletType): boolean {
  if (wallet.isMainAccount) return true;

  if (wallet.currency === 'USD') {
    const bridgeScStatus = getBankPartnerStatus(
      wallet,
      BRIDGE_ACCOUNT_PARTNERS.BRIDGE_SC
    );
    return Boolean(
      bridgeScStatus?.status &&
        ['AVAILABLE', 'IN_PROGRESS', 'CREATED'].includes(bridgeScStatus.status)
    );
  }

  return false;
}

export const validateKyc = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): Promise<boolean | RouteLocationRaw | string | void> => {
  // Si la ruta no requiere autenticación, no es necesario validar KYC
  if (!to.matched.some((record) => record.meta.requiresAuth)) {
    return true;
  }

  const remoteConfigStore = useRemoteConfigStore();
  const storeAccount = useAccountStore();

  const shouldLockUserOnHomeByShareholders =
    remoteConfigStore.getRemoteConfigValue(
      'feature_shareholder_modal_user_lock'
    );

  const isShareholderMandatoryColombia = remoteConfigStore.getFeatureFlag(
    'feature_shareholder_modal_mandatory_colombia'
  );
  const isShareholderUserLockColombia = remoteConfigStore.getFeatureFlag(
    'feature_shareholder_modal_user_lock_colombia'
  );
  const isShareholderMandatoryColombiaReview = remoteConfigStore.getFeatureFlag(
    'feature_shareholder_modal_mandatory_colombia_review'
  );
  const isShareholderUserLockColombiaReview = remoteConfigStore.getFeatureFlag(
    'feature_shareholder_modal_user_lock_colombia_review'
  );

  // Si no tenemos kyc lo consultamos
  if (!storeAccount?.kycStatus?.b2bUser && !storeAccount.kycStatus?.company) {
    try {
      const { data } = await authClient.getKycStatus();
      storeAccount.setKyc(data);
    } catch (error) {
      // Si no tiene KYC, lo redirigimos a login
      logOut();
      return { name: 'log-in' };
    }
  }

  // Si el KYC está bloqueado, lo redirigimos a login
  if (storeAccount?.isKycCompanyFullyBlocked) {
    logOut();
    return { name: 'log-in' };
  }

  const { isRouteExcluded } = useAccessExclusions();
  const routeExclusion = isRouteExcluded(from.name ?? null, to.name ?? '');

  if (routeExclusion.isExcluded) {
    if (routeExclusion.redirectRouteName === from.name && from.name) {
      return from;
    }
    if (!routeExclusion.redirectRouteName) return false;
    
    return { name: routeExclusion.redirectRouteName };
  }

  const storeWallet = useWalletStore();

  const isUserLocked = shouldLockUserOnHomeByShareholders.value || false;
  const companyCountry =
    storeAccount.kycStatus?.company?.addressCountry?.country || '';
  const isChileGlobal81 = isCompanyGlobal81Chile(
    companyCountry,
    storeWallet.getMainWallet
  );
  const companyId = String(storeAccount.kycStatus?.company?.id ?? '');
  const isAdmin =
    (storeAccount.kycStatus?.b2bUser?.level ?? 0) >= ADMIN_USER_LEVEL;
  const baseAllowedRoutes = [
    'movements',
    'verify-phone-otp',
    'onboarding-invitation',
    'jumio-regularization-confirm',
    'jumio-regularization-pending',
  ];
  const allowedRoutes = isAdmin
    ? [...baseAllowedRoutes, 'shareholders-declaration']
    : baseAllowedRoutes;

  if (
    !isPaymentCallbackRoute(to) &&
    !allowedRoutes.includes(String(to.name)) &&
    isUserLocked &&
    storeAccount.isKycCompanyCompleted &&
    !storeWallet.isGlobalCard &&
    companyCountry &&
    !SHAREHOLDERS_MODAL_EXCLUDED_COUNTRIES.includes(companyCountry) &&
    !SHAREHOLDERS_MODAL_WHITELISTED_COMPANY_IDS.includes(companyId) &&
    (isChileGlobal81 || !storeAccount.kycStatus?.company?.hasBeneficiaryFile)
  ) {
    return { name: 'movements' };
  }

  const companyColombia = storeAccount.kycStatus?.company;
  const isColombiaOperative = isCompanyComplete(companyColombia);
  const isColombiaReviewCohort = companyColombia?.complianceStatus !== 'NORMAL';

  // El hard-lock de cada cohorte exige su par mandatory && lock, evitando
  // encerrar al usuario sin modal (asimetría). El cohorte en revisión usa las
  // flags _review, apagadas durante el periodo de gracia (23-jul-2026).
  const isColombiaHardLocked = isColombiaReviewCohort
    ? isShareholderMandatoryColombiaReview.value.enabled &&
      isShareholderUserLockColombiaReview.value.enabled
    : isShareholderMandatoryColombia.value.enabled &&
      isShareholderUserLockColombia.value.enabled;

  const allowedRoutesColombia = isAdmin
    ? [...baseAllowedRoutes, 'shareholders-declaration-colombia']
    : baseAllowedRoutes;

  const shouldEvaluateColombiaLock =
    !isPaymentCallbackRoute(to) &&
    !allowedRoutesColombia.includes(String(to.name)) &&
    companyCountry === SHAREHOLDERS_MODAL_COUNTRY_CO &&
    isColombiaOperative &&
    isColombiaHardLocked &&
    !storeWallet.isGlobalCard &&
    !SHAREHOLDERS_MODAL_WHITELISTED_COMPANY_IDS.includes(companyId);

  if (shouldEvaluateColombiaLock) {
    const strictBeneficiaryStatus =
      await storeAccount.loadStrictBeneficiaryStatus();

    if (strictBeneficiaryStatus === null) {
      toast.error(SHAREHOLDERS_VERIFICATION_ERROR_MESSAGE);
      return { name: 'movements' };
    }

    if (!strictBeneficiaryStatus) {
      return { name: 'movements' };
    }
  }

  // Si intenta ir a recargar dinero pero su wallet principal es dólares y no las opciones por defecto
  if (
    !canCompanyRecharge(storeAccount.kycStatus) &&
    to.name == 'recharge-index'
  ) {
    if (!to.query.wallet) {
      return { name: 'movements' };
    }

    const wallet = storeWallet.wallets.find(
      (w) => w.walletId === Number(to.query.wallet)
    );

    if (!wallet || !canWalletRecharge(wallet)) {
      return { name: 'movements' };
    }
  }

  // Verificar KYC
  const kycRedirect = verifyKycStatus(to, storeAccount.kycStatus);
  if (kycRedirect) {
    return kycRedirect;
  }

  // Validar acceso a onboarding
  const onboardingRedirect = validateOnboardingAccess(
    to,
    storeAccount.kycStatus
  );
  if (onboardingRedirect) {
    return onboardingRedirect;
  }

  // Permitimos la navegación y que se siga ejecutando los siguientes guardianes
  return true;
};
